import { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Box, Button, Container, Header, SpaceBetween } from '@cloudscape-design/components';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 * 
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Container>
          <SpaceBetween size="l">
            <Header variant="h1">Something went wrong</Header>
            
            <Alert
              type="error"
              header="Application Error"
              action={
                <Button onClick={this.handleReset}>
                  Try again
                </Button>
              }
            >
              An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.
            </Alert>

            {import.meta.env.DEV && this.state.error && (
              <Box>
                <Header variant="h3">Error Details (Development Only)</Header>
                <Box variant="code">
                  <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {this.state.error.toString()}
                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                  </pre>
                </Box>
              </Box>
            )}
          </SpaceBetween>
        </Container>
      );
    }

    return this.props.children;
  }
}
